// EmailAccountCard — single card representing one email purpose.
//
// Modes:
//   - SMTP outbound (purpose: notifications | receipts | invoices)
//   - IMAP inbound (purpose: import)
//
// Behaviour:
//   - View mode shows status, active toggle, last-tested badge,
//     and a "Test Connection" flow that reveals an inline input.
//   - "Edit" reveals the form. Save calls PUT; Cancel resets local state.
//   - Server validates the full credential payload on every PUT, so the
//     password field MUST be re-entered whenever any field changes (the
//     server-side Zod schema requires `smtp_password` / `imap_password`
//     to be a non-empty string). Leaving the password blank surfaces an
//     inline error rather than overwriting with null.

import { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Input } from '../../../../components/form/Input';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import {
  useEmailSettings,
  useTestEmailConnection,
  useUpdateEmailSettings,
} from '../hooks/useCommunicationSettings';
import type {
  EmailPurpose,
  EmailSettings,
  UpsertImapEmailInput,
  UpsertSmtpEmailInput,
} from '../../../../types/api';
import styles from './EmailAccountCard.module.css';

export interface EmailAccountCardProps {
  purpose: EmailPurpose;
  title: string;
  description: string;
}

interface SmtpFormState {
  smtp_host: string;
  smtp_port: string;
  smtp_username: string;
  smtp_password: string;
  smtp_from_name: string;
  smtp_from_email: string;
  smtp_encryption: 'ssl' | 'tls' | 'none';
}

interface ImapFormState {
  imap_host: string;
  imap_port: string;
  imap_username: string;
  imap_password: string;
  imap_folder: string;
}

const EMPTY_SMTP: SmtpFormState = {
  smtp_host: '',
  smtp_port: '587',
  smtp_username: '',
  smtp_password: '',
  smtp_from_name: '',
  smtp_from_email: '',
  smtp_encryption: 'tls',
};

const EMPTY_IMAP: ImapFormState = {
  imap_host: '',
  imap_port: '993',
  imap_username: '',
  imap_password: '',
  imap_folder: 'INBOX',
};

function smtpFromSettings(s: EmailSettings): SmtpFormState {
  return {
    smtp_host: s.smtp_host ?? '',
    smtp_port: s.smtp_port !== null ? String(s.smtp_port) : '587',
    smtp_username: s.smtp_username ?? '',
    smtp_password: '',
    smtp_from_name: s.smtp_from_name ?? '',
    smtp_from_email: s.smtp_from_email ?? '',
    smtp_encryption: s.smtp_encryption ?? 'tls',
  };
}

function imapFromSettings(s: EmailSettings): ImapFormState {
  return {
    imap_host: s.imap_host ?? '',
    imap_port: s.imap_port !== null ? String(s.imap_port) : '993',
    imap_username: s.imap_username ?? '',
    imap_password: '',
    imap_folder: s.imap_folder ?? 'INBOX',
  };
}

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = Date.now() - t;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} hr ago`;
  return `${Math.floor(sec / 86400)} days ago`;
}

export function EmailAccountCard({
  purpose,
  title,
  description,
}: EmailAccountCardProps): JSX.Element {
  const isImport = purpose === 'import';
  const query = useEmailSettings(purpose);
  const update = useUpdateEmailSettings(purpose);
  const test = useTestEmailConnection(purpose);

  const [editing, setEditing] = useState(false);
  const [smtpForm, setSmtpForm] = useState<SmtpFormState>(EMPTY_SMTP);
  const [imapForm, setImapForm] = useState<ImapFormState>(EMPTY_IMAP);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testMessage, setTestMessage] = useState<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);

  // Reset form state whenever the loaded record changes.
  useEffect(() => {
    if (!query.data) return;
    if (isImport) setImapForm(imapFromSettings(query.data));
    else setSmtpForm(smtpFromSettings(query.data));
  }, [query.data, isImport]);

  function startEdit() {
    setSaveError(null);
    setEditing(true);
  }
  function cancelEdit() {
    setSaveError(null);
    if (query.data) {
      if (isImport) setImapForm(imapFromSettings(query.data));
      else setSmtpForm(smtpFromSettings(query.data));
    } else {
      if (isImport) setImapForm(EMPTY_IMAP);
      else setSmtpForm(EMPTY_SMTP);
    }
    setEditing(false);
  }

  async function handleSave() {
    setSaveError(null);
    if (isImport) {
      if (
        !imapForm.imap_host ||
        !imapForm.imap_port ||
        !imapForm.imap_username ||
        !imapForm.imap_folder
      ) {
        setSaveError('Please fill in every field.');
        return;
      }
      // Blank password = "keep existing" — server reuses the stored
      // ciphertext. Required only when this is a brand-new config
      // (server returns 400 password_required_for_new_config in that
      // case, surfaced via the mutation's onError).
      const body: UpsertImapEmailInput = {
        imap_host: imapForm.imap_host,
        imap_port: Number(imapForm.imap_port),
        imap_username: imapForm.imap_username,
        imap_password: imapForm.imap_password.length > 0 ? imapForm.imap_password : null,
        imap_folder: imapForm.imap_folder,
      };
      await update.mutateAsync(body, {
        onSuccess: () => setEditing(false),
        onError: (err) => setSaveError(extractSaveError(err)),
      });
      return;
    }

    if (
      !smtpForm.smtp_host ||
      !smtpForm.smtp_port ||
      !smtpForm.smtp_username ||
      !smtpForm.smtp_from_name ||
      !smtpForm.smtp_from_email
    ) {
      setSaveError('Please fill in every field.');
      return;
    }
    const body: UpsertSmtpEmailInput = {
      smtp_host: smtpForm.smtp_host,
      smtp_port: Number(smtpForm.smtp_port),
      smtp_username: smtpForm.smtp_username,
      smtp_password: smtpForm.smtp_password.length > 0 ? smtpForm.smtp_password : null,
      smtp_from_name: smtpForm.smtp_from_name,
      smtp_from_email: smtpForm.smtp_from_email,
      smtp_encryption: smtpForm.smtp_encryption,
    };
    await update.mutateAsync(body, {
      onSuccess: () => setEditing(false),
      onError: (err) => setSaveError(extractSaveError(err)),
    });
  }

  async function handleToggleActive(next: boolean) {
    if (!query.data) return;
    setSaveError(null);
    // Send null password — server keeps the existing ciphertext, so
    // toggling active alone no longer requires re-typing the secret.
    if (isImport) {
      const body: UpsertImapEmailInput = {
        imap_host: query.data.imap_host ?? '',
        imap_port: query.data.imap_port ?? 993,
        imap_username: query.data.imap_username ?? '',
        imap_password: imapForm.imap_password.length > 0 ? imapForm.imap_password : null,
        imap_folder: query.data.imap_folder ?? 'INBOX',
        is_active: next,
      };
      await update.mutateAsync(body);
      return;
    }
    const body: UpsertSmtpEmailInput = {
      smtp_host: query.data.smtp_host ?? '',
      smtp_port: query.data.smtp_port ?? 587,
      smtp_username: query.data.smtp_username ?? '',
      smtp_password: smtpForm.smtp_password.length > 0 ? smtpForm.smtp_password : null,
      smtp_from_name: query.data.smtp_from_name ?? '',
      smtp_from_email: query.data.smtp_from_email ?? '',
      smtp_encryption: query.data.smtp_encryption ?? 'tls',
      is_active: next,
    };
    await update.mutateAsync(body);
  }

  function extractSaveError(err: unknown): string {
    const message = err instanceof Error ? err.message : '';
    if (message.includes('password_required_for_new_config')) {
      return 'A password is required when configuring this account for the first time.';
    }
    return 'Save failed. Check the values and retry.';
  }

  async function handleTest() {
    setTestMessage(null);
    if (!isImport && !testEmail) {
      setTestMessage({
        kind: 'error',
        text: 'Enter an email address to send the test to.',
      });
      return;
    }
    try {
      const result = await test.mutateAsync({
        test_email: isImport ? undefined : testEmail,
      });
      if (result.success) {
        setTestMessage({
          kind: 'success',
          text: isImport
            ? 'Inbox connected successfully.'
            : `Test email sent to ${testEmail}.`,
        });
      } else {
        setTestMessage({
          kind: 'error',
          text: result.message || 'Test failed.',
        });
      }
    } catch {
      setTestMessage({
        kind: 'error',
        text: 'Could not run the test. Try again.',
      });
    }
  }

  const isLoading = query.isLoading;
  // Server returns 200 with `configured: false` (not 404) for unset
  // purposes — preferred signal. Fall back to host-presence checks for
  // older API responses + the error case.
  const notConfigured =
    query.error !== null ||
    (!isLoading && !query.data) ||
    (query.data && query.data.configured === false) ||
    (query.data &&
      !isImport &&
      query.data.smtp_host === null) ||
    (query.data && isImport && query.data.imap_host === null);

  return (
    <Card title={title}>
      <p className={styles.description}>{description}</p>

      {isLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <>
          <div className={styles.statusRow}>
            <span
              className={
                notConfigured ? styles.statusBad : styles.statusOk
              }
              data-testid={`status-${purpose}`}
            >
              {notConfigured ? 'Not configured' : 'Configured ✓'}
            </span>
            {query.data && !notConfigured ? (
              <label className={styles.activeToggle}>
                <input
                  type="checkbox"
                  checked={query.data.is_active}
                  onChange={(e) => void handleToggleActive(e.target.checked)}
                  aria-label={`${title} active`}
                />
                <span>{query.data.is_active ? 'Active' : 'Inactive'}</span>
              </label>
            ) : null}
          </div>

          {!editing ? (
            <div className={styles.viewBlock}>
              {query.data && !notConfigured ? (
                <dl className={styles.summary}>
                  {!isImport ? (
                    <>
                      <div className={styles.summaryRow}>
                        <dt>Host</dt>
                        <dd>
                          {query.data.smtp_host}
                          {query.data.smtp_port
                            ? ` : ${query.data.smtp_port}`
                            : ''}
                        </dd>
                      </div>
                      <div className={styles.summaryRow}>
                        <dt>From</dt>
                        <dd>
                          {query.data.smtp_from_name
                            ? `${query.data.smtp_from_name} <${query.data.smtp_from_email ?? ''}>`
                            : (query.data.smtp_from_email ?? '—')}
                        </dd>
                      </div>
                      <div className={styles.summaryRow}>
                        <dt>Encryption</dt>
                        <dd>
                          {(query.data.smtp_encryption ?? 'tls').toUpperCase()}
                        </dd>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.summaryRow}>
                        <dt>Host</dt>
                        <dd>
                          {query.data.imap_host}
                          {query.data.imap_port
                            ? ` : ${query.data.imap_port}`
                            : ''}
                        </dd>
                      </div>
                      <div className={styles.summaryRow}>
                        <dt>Folder</dt>
                        <dd>{query.data.imap_folder ?? 'INBOX'}</dd>
                      </div>
                      <div className={styles.summaryRow}>
                        <dt>Username</dt>
                        <dd>{query.data.imap_username ?? '—'}</dd>
                      </div>
                    </>
                  )}
                </dl>
              ) : null}

              <div className={styles.actions}>
                <Button
                  variant="secondary"
                  icon={Pencil}
                  onClick={startEdit}
                  size="sm"
                >
                  {notConfigured ? 'Configure' : 'Edit'}
                </Button>
              </div>

              {!notConfigured && query.data ? (
                <div className={styles.testBlock}>
                  {!testOpen ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setTestOpen(true)}
                    >
                      Test Connection
                    </Button>
                  ) : (
                    <div className={styles.testForm}>
                      {!isImport ? (
                        <Input
                          label="Send test to"
                          variant="email"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          placeholder="admin@example.com"
                        />
                      ) : (
                        <p className={styles.muted}>
                          Tests the IMAP connection — no email is sent.
                        </p>
                      )}
                      <div className={styles.testButtons}>
                        <Button
                          variant="primary"
                          size="sm"
                          loading={test.isPending}
                          onClick={() => void handleTest()}
                        >
                          {isImport ? 'Test Inbox' : 'Send Test'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTestOpen(false);
                            setTestMessage(null);
                            setTestEmail('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                      {testMessage ? (
                        <p
                          className={
                            testMessage.kind === 'success'
                              ? styles.testSuccess
                              : styles.testError
                          }
                          role={
                            testMessage.kind === 'error' ? 'alert' : 'status'
                          }
                        >
                          {testMessage.kind === 'success' ? '✓ ' : '✗ '}
                          {testMessage.text}
                        </p>
                      ) : null}
                    </div>
                  )}
                  {query.data.last_tested_at ? (
                    <p className={styles.lastTested}>
                      Last tested: {relativeTime(query.data.last_tested_at)}{' '}
                      —{' '}
                      {query.data.last_test_result === 'success'
                        ? '✓ success'
                        : '✗ failed'}
                      {query.data.last_test_message
                        ? ` — ${query.data.last_test_message}`
                        : ''}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            <form
              className={styles.form}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSave();
              }}
            >
              {!isImport ? (
                <>
                  <div className={styles.row}>
                    <Input
                      label="SMTP Host"
                      required
                      value={smtpForm.smtp_host}
                      onChange={(e) =>
                        setSmtpForm({ ...smtpForm, smtp_host: e.target.value })
                      }
                    />
                    <Input
                      label="Port"
                      variant="number"
                      required
                      value={smtpForm.smtp_port}
                      onChange={(e) =>
                        setSmtpForm({ ...smtpForm, smtp_port: e.target.value })
                      }
                    />
                  </div>
                  <div className={styles.row}>
                    <Input
                      label="From Name"
                      required
                      value={smtpForm.smtp_from_name}
                      onChange={(e) =>
                        setSmtpForm({
                          ...smtpForm,
                          smtp_from_name: e.target.value,
                        })
                      }
                    />
                    <Input
                      label="From Email"
                      variant="email"
                      required
                      value={smtpForm.smtp_from_email}
                      onChange={(e) =>
                        setSmtpForm({
                          ...smtpForm,
                          smtp_from_email: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className={styles.row}>
                    <Input
                      label="Username"
                      required
                      value={smtpForm.smtp_username}
                      onChange={(e) =>
                        setSmtpForm({
                          ...smtpForm,
                          smtp_username: e.target.value,
                        })
                      }
                    />
                    <Input
                      label="Password"
                      variant="password"
                      placeholder="Leave blank to keep existing"
                      value={smtpForm.smtp_password}
                      onChange={(e) =>
                        setSmtpForm({
                          ...smtpForm,
                          smtp_password: e.target.value,
                        })
                      }
                      helper="Blank = keep the previously-saved password. Required only when configuring for the first time."
                    />
                  </div>
                  <div className={styles.row}>
                    <label className={styles.selectField}>
                      <span>Encryption</span>
                      <select
                        value={smtpForm.smtp_encryption}
                        onChange={(e) =>
                          setSmtpForm({
                            ...smtpForm,
                            smtp_encryption: e.target
                              .value as SmtpFormState['smtp_encryption'],
                          })
                        }
                      >
                        <option value="tls">TLS / STARTTLS</option>
                        <option value="ssl">SSL</option>
                        <option value="none">None</option>
                      </select>
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.row}>
                    <Input
                      label="IMAP Host"
                      required
                      value={imapForm.imap_host}
                      onChange={(e) =>
                        setImapForm({ ...imapForm, imap_host: e.target.value })
                      }
                    />
                    <Input
                      label="Port"
                      variant="number"
                      required
                      value={imapForm.imap_port}
                      onChange={(e) =>
                        setImapForm({ ...imapForm, imap_port: e.target.value })
                      }
                    />
                  </div>
                  <div className={styles.row}>
                    <Input
                      label="Username"
                      required
                      value={imapForm.imap_username}
                      onChange={(e) =>
                        setImapForm({
                          ...imapForm,
                          imap_username: e.target.value,
                        })
                      }
                    />
                    <Input
                      label="Password"
                      variant="password"
                      placeholder="Leave blank to keep existing"
                      value={imapForm.imap_password}
                      onChange={(e) =>
                        setImapForm({
                          ...imapForm,
                          imap_password: e.target.value,
                        })
                      }
                      helper="Blank = keep the previously-saved password. Required only when configuring for the first time."
                    />
                  </div>
                  <div className={styles.row}>
                    <Input
                      label="Folder"
                      required
                      value={imapForm.imap_folder}
                      onChange={(e) =>
                        setImapForm({
                          ...imapForm,
                          imap_folder: e.target.value,
                        })
                      }
                      helper="Default: INBOX"
                    />
                  </div>
                </>
              )}

              {saveError ? (
                <p className={styles.saveError} role="alert">
                  {saveError}
                </p>
              ) : null}

              <div className={styles.formButtons}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={update.isPending}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={cancelEdit}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </Card>
  );
}
